import { useState, useEffect, useRef } from 'react';
import DateInput from './DateInput';
import { supabase } from '../lib/supabase';
import { fmt$ } from '../utils/formatters';
import { sn } from '../utils/formatters';
import { parseLocalDate } from '../utils/dates';
import { extractInvoiceData, matchToIngredients, normalizeAlias } from '../lib/invoiceApi';
import { suggestEC } from '../lib/feeDetection';
import InvoiceOriginalViewer from './InvoiceOriginalViewer';
import IngredientDropdown from './IngredientDropdown';
import InspectName from './InspectName';

function makeBlankLine(){return{key:Math.random().toString(36).slice(2),description:'',matched_ingredient_id:'',matched_ingredient_name:'',ingSearch:'',unit:'CS',qty_ordered:'',unit_cost_paid:''};}

const PU_CATS=["Food","Beverages","Packaging","Paper Goods","Retail Goods"];
const PREPAID_UNITS=["CS","EA","BX","BAG","CASE","PKG","LB","OZ","GAL","QT","PT","DOZ","BTL"];

export default function Prepaid({orgId,uid,ings,setIngs,settings,t,si,sb,sh,sd,mn,batches,mi,setPg,setReturnTo,navigateToVendorExpenses,save,userRole}){
const[view,setView]=useState('list'); // 'list' | 'create' | 'detail'
const[orders,setOrders]=useState([]);
const[lineCounts,setLineCounts]=useState({});
const[loading,setLoading]=useState(true);

// Detail state
const[detailOrder,setDetailOrder]=useState(null);
const[detailLines,setDetailLines]=useState([]);
const[detailDeliveries,setDetailDeliveries]=useState([]);
const[detailLoading,setDetailLoading]=useState(false);
const[deliveryQtys,setDeliveryQtys]=useState({});
const[deliveryDate,setDeliveryDate]=useState(()=>new Date().toISOString().split('T')[0]);
const[deliveryCat,setDeliveryCat]=useState('Food');
const[deliverySaving,setDeliverySaving]=useState(false);
const[deliveryErr,setDeliveryErr]=useState('');
const[deliveryMsg,setDeliveryMsg]=useState('');
const[closeLineId,setCloseLineId]=useState(null);
const[closeReason,setCloseReason]=useState('');
const[deleteConfirm,setDeleteConfirm]=useState(null); // {id,vendor,purchaseTotal,feeTotal}
const[deleting,setDeleting]=useState(false);

async function deleteOrder(orderId){
  setDeleting(true);
  try{
    // Mark delivery purchases as superseded
    const{data:deliveries}=await supabase.from('prepaid_deliveries').select('purchase_id').eq('prepaid_order_id',orderId);
    const puIds=(deliveries||[]).map(d=>d.purchase_id).filter(Boolean);
    if(puIds.length){
      const{error:puErr}=await supabase.from('purchases').update({is_superseded:true}).in('id',puIds);
      if(puErr)throw new Error('Failed to supersede purchases: '+puErr.message);
    }
    // Mark fee expenses as superseded (written at order creation, vendor matches)
    const order=orders.find(o=>o.id===orderId);
    if(order){
      const{error:ceErr}=await supabase.from('categorized_expenses').update({is_superseded:true}).eq('org_id',orgId).eq('vendor',order.vendor).eq('date',order.date_paid).eq('category','Fees').eq('is_superseded',false);
      if(ceErr)throw new Error('Failed to supersede fee expenses: '+ceErr.message);
    }
    // Delete deliveries, line items, then order
    await supabase.from('prepaid_deliveries').delete().eq('prepaid_order_id',orderId);
    await supabase.from('prepaid_line_items').delete().eq('prepaid_order_id',orderId);
    await supabase.from('prepaid_orders').delete().eq('id',orderId);
    setDeleteConfirm(null);
    setView('list');
    await loadOrders();
  }catch(e){alert('Delete failed: '+e.message);}
  setDeleting(false);
}

async function loadOrders(){
  setLoading(true);
  const{data:ords}=await supabase.from('prepaid_orders').select('*').eq('org_id',orgId).order('date_paid',{ascending:false});
  if(ords?.length){
    setOrders(ords);
    const ids=ords.map(o=>o.id);
    const{data:lines}=await supabase.from('prepaid_line_items').select('prepaid_order_id,qty_ordered,unit_cost_paid').in('prepaid_order_id',ids);
    const counts={};
    if(lines){for(const l of lines){if(!counts[l.prepaid_order_id])counts[l.prepaid_order_id]={count:0,total:0};counts[l.prepaid_order_id].count++;counts[l.prepaid_order_id].total+=(parseFloat(l.qty_ordered)||0)*(parseFloat(l.unit_cost_paid)||0);}}
    setLineCounts(counts);
  }else{setOrders([]);setLineCounts({});}
  setLoading(false);
}
useEffect(()=>{if(orgId)loadOrders();},[orgId]);

async function openDetail(order){
  setDetailLoading(true);setDetailOrder(order);setView('detail');
  setDeliveryErr('');setDeliveryMsg('');setDeliveryDate(new Date().toISOString().split('T')[0]);
  const{data:li}=await supabase.from('prepaid_line_items').select('*').eq('prepaid_order_id',order.id).order('created_at');
  setDetailLines(li||[]);
  // Default category from first matched ingredient's expense_category
  const firstMatched=(li||[]).find(l=>l.matched_ingredient_id);
  const matchedIg=firstMatched?(ings||[]).find(x=>x.id===firstMatched.matched_ingredient_id):null;
  const defaultCat=matchedIg?.ec&&PU_CATS.includes(matchedIg.ec)?matchedIg.ec:'Food';
  setDeliveryCat(defaultCat);
  const{data:del}=await supabase.from('prepaid_deliveries').select('*').eq('prepaid_order_id',order.id);
  setDetailDeliveries(del||[]);
  // Init delivery qtys to 0
  const qtys={};(li||[]).forEach(l=>{qtys[l.id]='';});
  setDeliveryQtys(qtys);
  setDetailLoading(false);
}

function getDelivered(lineId){return detailDeliveries.filter(d=>d.prepaid_line_item_id===lineId).reduce((s,d)=>s+(parseFloat(d.qty_delivered)||0),0);}

async function submitDelivery(){
  setDeliveryErr('');setDeliveryMsg('');
  const entries=detailLines.filter(l=>l.line_status!=='short_closed').map(l=>{
    const qty=parseFloat(deliveryQtys[l.id])||0;
    const outstanding=(parseFloat(l.qty_ordered)||0)-getDelivered(l.id);
    return{line:l,qty,outstanding};
  }).filter(e=>e.qty>0);
  if(!entries.length){setDeliveryErr('Enter a quantity for at least one line.');return;}
  const overLimit=entries.find(e=>e.qty>e.outstanding+0.001);
  if(overLimit){setDeliveryErr(`${overLimit.line.description}: can't deliver more than ${overLimit.outstanding} outstanding.`);return;}
  if(!deliveryDate){setDeliveryErr('Delivery date is required.');return;}
  setDeliverySaving(true);
  try{
    for(let i=0;i<entries.length;i++){
      const{line,qty}=entries[i];
      const delId=`pd${Date.now()}_${i}`;
      const puId=`pu${Date.now()}_${i}`;
      const amt=Math.round(qty*parseFloat(line.unit_cost_paid)*100)/100;
      // Insert purchase
      const{error:puErr}=await supabase.from('purchases').insert({id:puId,user_id:uid,org_id:orgId,vendor:detailOrder.vendor,amount:amt,date:deliveryDate,category:deliveryCat,prepaid_order_id:detailOrder.id});
      if(puErr)throw new Error(puErr.message);
      // Insert delivery with purchase_id link
      const{error:delErr}=await supabase.from('prepaid_deliveries').insert({id:delId,prepaid_line_item_id:line.id,prepaid_order_id:detailOrder.id,org_id:orgId,qty_delivered:qty,delivery_date:deliveryDate,purchase_id:puId});
      if(delErr)throw new Error(delErr.message);
      // Price-sheet update on FIRST delivery for this line (mirror invoice confirm pattern)
      if(line.matched_ingredient_id){
        const priorDels=detailDeliveries.filter(d=>d.prepaid_line_item_id===line.id);
        if(priorDels.length===0){
          const ig=(ings||[]).find(x=>x.id===line.matched_ingredient_id);
          const newCasePrice=parseFloat(line.unit_cost_paid)||0;
          if(ig&&Math.abs(ig.cp-newCasePrice)>0.01){
            await supabase.from('ingredients').update({case_price:newCasePrice}).eq('id',ig.id);
            await supabase.from('price_history').insert({user_id:uid,org_id:orgId,ingredient_id:ig.id,old_price:ig.cp,new_price:newCasePrice,source:'prepaid'});
            if(setIngs)setIngs(p=>p.map(x=>x.id===ig.id?{...x,prevCp:x.cp,cp:newCasePrice}:x));
          }
        }
      }
    }
    // Update line statuses
    for(const l of detailLines){
      if(l.line_status==='short_closed')continue;
      const totalDel=getDelivered(l.id)+(entries.find(e=>e.line.id===l.id)?.qty||0);
      const ordered=parseFloat(l.qty_ordered)||0;
      const newStatus=totalDel>=ordered?'complete':totalDel>0?'partial':'awaiting';
      if(newStatus!==l.line_status)await supabase.from('prepaid_line_items').update({line_status:newStatus}).eq('id',l.id);
    }
    // Update order status
    const allLines=detailLines.map(l=>{
      if(l.line_status==='short_closed')return'short_closed';
      const totalDel=getDelivered(l.id)+(entries.find(e=>e.line.id===l.id)?.qty||0);
      return totalDel>=(parseFloat(l.qty_ordered)||0)?'complete':totalDel>0?'partial':'awaiting';
    });
    const active=allLines.filter(s=>s!=='short_closed');
    const orderStatus=active.length===0?'complete':active.every(s=>s==='complete')?'complete':active.some(s=>s!=='awaiting')?'partial':'awaiting';
    await supabase.from('prepaid_orders').update({status:orderStatus}).eq('id',detailOrder.id);
    setDeliveryMsg(`Recorded ${entries.length} line${entries.length>1?'s':''} delivered.`);
    setTimeout(()=>setDeliveryMsg(''),4000);
    await openDetail({...detailOrder,status:orderStatus});
    await loadOrders();
  }catch(e){setDeliveryErr(e.message||'Failed to record delivery.');}
  setDeliverySaving(false);
}

async function shortCloseLine(){
  if(!closeLineId||!closeReason.trim()){setDeliveryErr('Close reason is required.');return;}
  await supabase.from('prepaid_line_items').update({line_status:'short_closed',close_reason:closeReason.trim()}).eq('id',closeLineId);
  // Recompute order status
  const updatedLines=detailLines.map(l=>l.id===closeLineId?{...l,line_status:'short_closed'}:l);
  const active=updatedLines.filter(l=>l.line_status!=='short_closed');
  const orderStatus=active.length===0?'complete':active.every(l=>{const del=getDelivered(l.id);return del>=(parseFloat(l.qty_ordered)||0);})?'complete':active.some(l=>getDelivered(l.id)>0)?'partial':'awaiting';
  await supabase.from('prepaid_orders').update({status:orderStatus}).eq('id',detailOrder.id);
  setCloseLineId(null);setCloseReason('');
  await openDetail({...detailOrder,status:orderStatus});
  await loadOrders();
}

// Create form state
const[vendor,setVendor]=useState('');
const[datePaid,setDatePaid]=useState(()=>new Date().toISOString().split('T')[0]);
const[notes,setNotes]=useState('');
const[lines,setLines]=useState(()=>[makeBlankLine(),makeBlankLine()]);
const[saving,setSaving]=useState(false);
const[formErr,setFormErr]=useState('');
const[uploadFile,setUploadFile]=useState(null);
const[uploading,setUploading]=useState(false);
const fileRef=useRef(null);
const createOrderId=useRef(`pp${Date.now()}`);
const vendorNames=(settings?.vendors||[]).sort();
function updateLine(key,field,val){setLines(p=>p.map(l=>l.key===key?{...l,[field]:val}:l));}
function removeLine(key){setLines(p=>p.filter(l=>l.key!==key));}
function addLine(){setLines(p=>[...p,makeBlankLine()]);}
const orderTotal=lines.reduce((s,l)=>{const q=parseFloat(l.qty_ordered)||0;const c=parseFloat(l.unit_cost_paid)||0;return s+q*c;},0);

const[extracting,setExtracting]=useState(false);
const[detectedFees,setDetectedFees]=useState([]);
const[dragging,setDragging]=useState(false);
const dragCounter=useRef(0);
const extractRef=useRef(null);

async function handleFileSelect(file){
  if(!file)return;
  if(file.size>20*1024*1024){setFormErr('File too large (max 20MB)');return;}
  setUploading(true);setFormErr('');
  const ext=file.name.split('.').pop().toLowerCase()||'pdf';
  const path=`${orgId}/prepaid/${createOrderId.current}.${ext}`;
  const{error}=await supabase.storage.from('invoices').upload(path,file,{contentType:file.type||'application/pdf',upsert:true});
  if(error){setFormErr('Upload failed: '+error.message);setUploading(false);return;}
  setUploadFile({path,name:file.name});
  setUploading(false);
}

async function handleExtract(file){
  if(!file)return;
  if(file.size>20*1024*1024){setFormErr('File too large (max 20MB)');return;}
  setExtracting(true);setFormErr('');
  const mimeType=file.type||'application/pdf';
  const ext=file.name.split('.').pop().toLowerCase()||'pdf';
  const path=`${orgId}/prepaid/${createOrderId.current}.${ext}`;
  try{
    const{error:upErr}=await supabase.storage.from('invoices').upload(path,file,{contentType:mimeType,upsert:true});
    if(upErr)throw new Error('Upload failed: '+upErr.message);
    setUploadFile({path,name:file.name});
    const data=await extractInvoiceData(path,mimeType);
    const{data:aliasRows}=await supabase.from('ingredient_aliases').select('normalized_alias,ingredient_id').eq('org_id',orgId);
    const aliasMap=new Map((aliasRows||[]).map(r=>[r.normalized_alias,r.ingredient_id]));
    const matched=matchToIngredients(data.line_items||[],ings||[],data.vendor||'',aliasMap);
    // Pre-fill form
    if(data.vendor)setVendor(data.vendor);
    if(data.invoice_date)setDatePaid(data.invoice_date);
    if(data.invoice_number)setNotes(n=>n?n:`Invoice #${data.invoice_number}`);
    // Partition OCR lines: goods vs fees
    const goods=[];const fees=[];
    for(const item of matched){
      if(!item.description)continue;
      const ec=suggestEC(item.description,item.category);
      if(ec==='Fees'){const feeAmt=parseFloat(item.amount)||0;if(feeAmt>0)fees.push({desc:item.description,amt:feeAmt});}
      else{
        const qty=parseFloat(item.qty_shipped)||0;const amt=parseFloat(item.amount)||0;
        const caseCost=qty>0?Math.round(amt/qty*100)/100:(parseFloat(item.unit_price)||0);
        const ig=item.matched_ingredient_id?(ings||[]).find(x=>x.id===item.matched_ingredient_id):null;
        const ocrUnit=(item.unit||'').toUpperCase();
        goods.push({key:Math.random().toString(36).slice(2),description:item.description||'',matched_ingredient_id:item.matched_ingredient_id||'',matched_ingredient_name:ig?sn(ig.n):'',ingSearch:'',unit:PREPAID_UNITS.includes(ocrUnit)?ocrUnit:'CS',qty_ordered:String(qty||''),unit_cost_paid:String(caseCost||'')});
      }
    }
    if(goods.length)setLines(goods);
    setDetectedFees(fees);
  }catch(e){setFormErr(e.message||'Extraction failed.');}
  setExtracting(false);
}

async function saveOrder(){
  setFormErr('');
  if(!vendor.trim()){setFormErr('Vendor is required.');return;}
  if(!datePaid){setFormErr('Date paid is required.');return;}
  const validLines=lines.filter(l=>l.description.trim()&&(parseFloat(l.qty_ordered)||0)>0&&(parseFloat(l.unit_cost_paid)||0)>=0);
  if(!validLines.length){setFormErr('Add at least one line item with description and quantity.');return;}
  setSaving(true);
  try{
    const id=createOrderId.current;
    const{error:ordErr}=await supabase.from('prepaid_orders').insert({id,user_id:uid,org_id:orgId,vendor:vendor.trim(),date_paid:datePaid,file_url:uploadFile?.path||null,notes:notes.trim()||null,status:'awaiting'});
    if(ordErr)throw new Error(ordErr.message);
    const lineRows=validLines.map(l=>({id:`ppl${Date.now()}_${Math.random().toString(36).slice(2,6)}`,prepaid_order_id:id,org_id:orgId,matched_ingredient_id:l.matched_ingredient_id||null,description:l.description.trim(),unit:l.unit||'CS',qty_ordered:parseFloat(l.qty_ordered),unit_cost_paid:parseFloat(l.unit_cost_paid),line_status:'awaiting'}));
    const{error:lineErr}=await supabase.from('prepaid_line_items').insert(lineRows);
    if(lineErr)throw new Error(lineErr.message);
    // Write detected fees to categorized_expenses
    for(const fee of detectedFees){
      if(!fee.amt)continue;
      const expId=`ce${Date.now()}_${Math.random().toString(36).slice(2,6)}`;
      await supabase.from('categorized_expenses').insert({id:expId,user_id:uid,org_id:orgId,date:datePaid,category:'Fees',vendor:vendor.trim(),description:fee.desc,amount:fee.amt});
    }
    setDetectedFees([]);
    setVendor('');setDatePaid(new Date().toISOString().split('T')[0]);setNotes('');setLines([makeBlankLine(),makeBlankLine()]);setUploadFile(null);createOrderId.current=`pp${Date.now()}`;
    setView('list');await loadOrders();
  }catch(e){setFormErr(e.message||'Failed to save order.');}
  setSaving(false);
}


if(loading&&view==='list')return<div style={{textAlign:'center',padding:40,color:t.td}}>Loading...</div>;

// ===================== LIST VIEW =====================
if(view==='list')return(<div>
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
<h1 style={{fontSize:22,fontWeight:700,margin:0}}>Prepaid Orders</h1>
<button onClick={()=>setView('create')} style={{...sb,background:t.gn}}>+ New Prepaid Order</button>
</div>
{orders.length===0?<div style={{textAlign:'center',padding:60,color:t.td}}>
<div style={{fontSize:40,marginBottom:12}}>📦</div>
<div style={{fontSize:16,fontWeight:600,marginBottom:6}}>No prepaid orders yet</div>
<div style={{fontSize:13}}>Record advance payments to vendors and track deliveries as they arrive.</div>
</div>:
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden'}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>
{['Vendor','Date Paid','Items','Total','Status'].map(h=><th key={h} style={sh}>{h}</th>)}
</tr></thead>
<tbody>{orders.map(o=>{const lc=lineCounts[o.id]||{count:0,total:0};return(
<tr key={o.id} style={{borderBottom:`1px solid ${t.bd}`,cursor:'pointer'}} onClick={()=>openDetail(o)} onMouseOver={e=>e.currentTarget.style.background=t.sfh} onMouseOut={e=>e.currentTarget.style.background='transparent'}>
<td style={{...sd,fontWeight:500,fontSize:12}}>{o.vendor}</td>
<td style={{...sd,fontSize:11,color:t.tm}}>{parseLocalDate(o.date_paid).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</td>
<td style={{...sd,fontSize:11,color:t.td}}>{lc.count} item{lc.count!==1?'s':''}</td>
<td style={{...sd,fontFamily:mn,fontSize:12,fontWeight:600}}>${fmt$(lc.total)}</td>
<td style={sd}><span style={{fontSize:9,padding:'2px 8px',borderRadius:4,fontWeight:500,
  background:o.status==='awaiting'?'rgba(212,168,36,0.12)':o.status==='partial'?'rgba(59,130,246,0.12)':'rgba(45,184,122,0.12)',
  color:o.status==='awaiting'?t.yl:o.status==='partial'?t.bl:t.gn
}}>{o.status==='awaiting'?'Awaiting':o.status==='partial'?'Partial':'Delivered'}</span></td>
</tr>);})}</tbody>
</table></div>}
</div>);

// ===================== DETAIL VIEW =====================
if(view==='detail'){
if(detailLoading)return<div style={{textAlign:'center',padding:40,color:t.td}}>Loading order...</div>;
if(!detailOrder)return null;

const outstandingLiability=detailLines.filter(l=>l.line_status!=='short_closed').reduce((s,l)=>{const out=Math.max(0,(parseFloat(l.qty_ordered)||0)-getDelivered(l.id));return s+out*(parseFloat(l.unit_cost_paid)||0);},0);
const shortClosedLoss=detailLines.filter(l=>l.line_status==='short_closed').reduce((s,l)=>{const undelivered=Math.max(0,(parseFloat(l.qty_ordered)||0)-getDelivered(l.id));return s+undelivered*(parseFloat(l.unit_cost_paid)||0);},0);
const totalPaid=detailLines.reduce((s,l)=>s+(parseFloat(l.qty_ordered)||0)*(parseFloat(l.unit_cost_paid)||0),0);
const totalDeliveredValue=detailLines.reduce((s,l)=>s+getDelivered(l.id)*(parseFloat(l.unit_cost_paid)||0),0);

return(<div>
<div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
<button onClick={()=>{setView('list');setDetailOrder(null);}} style={{...sb,background:'transparent',color:t.tm,padding:'4px 0',fontSize:11}}>← Back to Orders</button>
<h1 style={{fontSize:20,fontWeight:700,margin:0}}><InspectName type="vendor" item={detailOrder.vendor} orgId={orgId} t={t} onEdit={()=>setPg('settings')} onViewExpenses={navigateToVendorExpenses} style={{color:t.bl}}>{detailOrder.vendor}</InspectName></h1>
<span style={{fontSize:9,padding:'2px 8px',borderRadius:4,fontWeight:500,
  background:detailOrder.status==='awaiting'?'rgba(212,168,36,0.12)':detailOrder.status==='partial'?'rgba(59,130,246,0.12)':'rgba(45,184,122,0.12)',
  color:detailOrder.status==='awaiting'?t.yl:detailOrder.status==='partial'?t.bl:t.gn
}}>{detailOrder.status==='awaiting'?'Awaiting':detailOrder.status==='partial'?'Partial':'Delivered'}</span>
{userRole==='owner'&&<button onClick={async()=>{
  // Calculate totals for confirmation
  const{data:dels}=await supabase.from('prepaid_deliveries').select('purchase_id').eq('prepaid_order_id',detailOrder.id);
  const puIds=(dels||[]).map(d=>d.purchase_id).filter(Boolean);
  let purchaseTotal=0;
  if(puIds.length){const{data:pus}=await supabase.from('purchases').select('amount').in('id',puIds).eq('is_superseded',false);purchaseTotal=(pus||[]).reduce((s,p)=>s+Number(p.amount),0);}
  const{data:fees}=await supabase.from('categorized_expenses').select('amount').eq('org_id',orgId).eq('vendor',detailOrder.vendor).eq('date',detailOrder.date_paid).eq('category','Fees').eq('is_superseded',false);
  const feeTotal=(fees||[]).reduce((s,f)=>s+Number(f.amount),0);
  setDeleteConfirm({id:detailOrder.id,vendor:detailOrder.vendor,purchaseTotal,feeTotal});
}} style={{...sb,background:'transparent',color:t.ac,fontSize:10,marginLeft:'auto'}}>Delete Order</button>}
</div>

{deleteConfirm&&<div style={{background:'rgba(200,55,45,0.06)',border:`1px solid ${t.ac}`,borderRadius:10,padding:16,marginBottom:16}}>
<div style={{fontSize:14,fontWeight:700,color:t.ac,marginBottom:8}}>Delete this prepaid order?</div>
<div style={{fontSize:12,color:t.td,marginBottom:12}}>
This will remove the order, all deliveries, and mark the following as superseded on the P&L:
{deleteConfirm.purchaseTotal>0&&<div style={{marginTop:4}}>Purchases (COGS): <strong style={{fontFamily:mn}}>−${fmt$(deleteConfirm.purchaseTotal)}</strong></div>}
{deleteConfirm.feeTotal>0&&<div style={{marginTop:2}}>Fee expenses: <strong style={{fontFamily:mn}}>−${fmt$(deleteConfirm.feeTotal)}</strong></div>}
{!deleteConfirm.purchaseTotal&&!deleteConfirm.feeTotal&&<div style={{marginTop:4,fontStyle:'italic'}}>No P&L impact (no deliveries recorded yet)</div>}
</div>
<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
<button onClick={()=>setDeleteConfirm(null)} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Cancel</button>
<button onClick={()=>deleteOrder(deleteConfirm.id)} disabled={deleting} style={{...sb,background:t.ac,color:'#fff',opacity:deleting?0.5:1}}>{deleting?'Deleting...':'Delete Order'}</button>
</div>
</div>}

{/* Summary cards */}
<div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:12,marginBottom:16}}>
{[['Total Paid','$'+fmt$(totalPaid),t.tx],['Delivered','$'+fmt$(totalDeliveredValue),t.gn],['Outstanding','$'+fmt$(outstandingLiability),outstandingLiability>0?t.yl:t.gn],['Written Off',shortClosedLoss>0?'$'+fmt$(shortClosedLoss):'—',shortClosedLoss>0?t.ac:t.td]].map(([l,v,c])=>
<div key={l} style={{background:t.sf,borderRadius:8,padding:'10px 12px'}}>
<div style={{fontSize:9,color:t.td,textTransform:'uppercase'}}>{l}</div>
<div style={{fontSize:16,fontWeight:700,fontFamily:mn,color:c,marginTop:2}}>{v}</div>
</div>)}
</div>

<div style={{fontSize:11,color:t.td,marginBottom:12}}>Paid {parseLocalDate(detailOrder.date_paid).toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}{detailOrder.notes?` — ${detailOrder.notes}`:''}</div>

<InvoiceOriginalViewer storagePath={detailOrder.file_url} t={t}/>

{deliveryMsg&&<div style={{background:'rgba(45,184,122,0.08)',border:`1px solid ${t.gn}`,borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:t.gn,fontWeight:500}}>{deliveryMsg}</div>}
{deliveryErr&&<div style={{background:'rgba(200,55,45,0.08)',border:`1px solid ${t.ac}`,borderRadius:8,padding:12,marginBottom:12,fontSize:12,color:t.ac}}>{deliveryErr}</div>}

{/* Line items + delivery inputs */}
{detailOrder.status!=='complete'&&<div style={{display:'flex',gap:16,alignItems:'end',flexWrap:'wrap',marginBottom:12,padding:'12px 16px',background:t.sf,borderRadius:10,border:`1px solid ${t.bd}`}}>
<div><label style={{fontSize:10,color:t.tm,fontWeight:600}}>Arrival Date</label><DateInput value={deliveryDate} onChange={e=>setDeliveryDate(e.target.value)} style={{...si,width:160,marginTop:4,display:'block'}}/></div>
<div><label style={{fontSize:10,color:t.tm,fontWeight:600}}>Category</label><select value={deliveryCat} onChange={e=>setDeliveryCat(e.target.value)} style={{...si,width:140,marginTop:4,display:'block'}}>{PU_CATS.map(c=><option key={c}>{c}</option>)}</select></div>
</div>}

<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,overflow:'hidden',marginBottom:16}}>
<table style={{width:'100%',borderCollapse:'collapse'}}>
<thead><tr style={{background:t.sf}}>
{['Item','Ingredient','Unit','Ordered','Delivered','Outstanding','Unit Cost','Status','Deliver Now'].map(h=><th key={h} style={{...sh,fontSize:9}}>{h}</th>)}
</tr></thead>
<tbody>{detailLines.map(l=>{
const del=getDelivered(l.id);const ordered=parseFloat(l.qty_ordered)||0;const out=Math.max(0,ordered-del);
const matched=l.matched_ingredient_id?(ings||[]).find(x=>x.id===l.matched_ingredient_id):null;
const isClosed=l.line_status==='short_closed';
return<tr key={l.id} style={{borderBottom:`1px solid ${t.bd}`,opacity:isClosed?0.5:1}}>
<td style={{...sd,fontWeight:500,fontSize:12}}>{l.description}</td>
<td style={{...sd,fontSize:10,color:matched?t.gn:t.td}}>{matched?<InspectName type="ingredient" item={matched} ings={ings} batches={batches} mi={mi} orgId={orgId} t={t} save={save} setIngs={setIngs} settings={settings} si={si} sb={sb} mn={mn}>{sn(matched.n)}</InspectName>:'—'}</td>
<td style={{...sd,fontSize:10}}>{l.unit}</td>
<td style={{...sd,fontFamily:mn,fontSize:11}}>{ordered}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,color:del>0?t.gn:t.td}}>{del}</td>
<td style={{...sd,fontFamily:mn,fontSize:11,color:out>0?t.yl:t.gn}}>{isClosed?'—':out}</td>
<td style={{...sd,fontFamily:mn,fontSize:11}}>${fmt$(parseFloat(l.unit_cost_paid)||0)}</td>
<td style={sd}>{isClosed?<div><span style={{fontSize:9,padding:'2px 6px',borderRadius:4,background:'rgba(139,143,158,0.12)',color:t.td}}>Closed</span>{l.close_reason&&<div style={{fontSize:8,color:t.td,marginTop:2}}>{l.close_reason}</div>}</div>:
<span style={{fontSize:9,padding:'2px 6px',borderRadius:4,
  background:l.line_status==='complete'?'rgba(45,184,122,0.12)':l.line_status==='partial'?'rgba(59,130,246,0.12)':'rgba(212,168,36,0.12)',
  color:l.line_status==='complete'?t.gn:l.line_status==='partial'?t.bl:t.yl
}}>{l.line_status==='complete'?'Complete':l.line_status==='partial'?'Partial':'Awaiting'}</span>}</td>
<td style={sd}>{isClosed||l.line_status==='complete'?'—':<div style={{display:'flex',gap:4,alignItems:'center'}}>
<input type="number" value={deliveryQtys[l.id]||''} onChange={e=>setDeliveryQtys(p=>({...p,[l.id]:e.target.value}))} placeholder="0" min="0" max={out} style={{...si,width:55,fontSize:11,textAlign:'center',fontFamily:mn}}/>
<button onClick={()=>{setCloseLineId(l.id);setCloseReason('');}} title="Close short" style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10,padding:'2px'}}>✕</button>
</div>}</td>
</tr>;})}</tbody>
</table>
</div>

{/* Short close modal */}
{closeLineId&&<div style={{background:t.sf,border:`1px solid ${t.bd}`,borderRadius:8,padding:16,marginBottom:16}}>
<div style={{fontSize:13,fontWeight:600,marginBottom:8}}>Close line short</div>
<div style={{fontSize:11,color:t.tm,marginBottom:8}}>This line will be marked as short-closed. The vendor won't deliver the remaining quantity. Already-delivered purchases are unaffected.</div>
<input value={closeReason} onChange={e=>setCloseReason(e.target.value)} placeholder="Reason for closing (required)" style={{...si,width:'100%',marginBottom:8,boxSizing:'border-box'}} autoFocus/>
<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
<button onClick={()=>{setCloseLineId(null);setCloseReason('');}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:11}}>Cancel</button>
<button onClick={shortCloseLine} disabled={!closeReason.trim()} style={{...sb,background:t.ac,fontSize:11,opacity:closeReason.trim()?1:0.4}}>Close Short</button>
</div>
</div>}

{/* Delivery submit */}
{detailOrder.status!=='complete'&&<div style={{display:'flex',justifyContent:'flex-end'}}>
<button onClick={submitDelivery} disabled={deliverySaving} style={{...sb,background:t.gn,padding:'10px 20px',opacity:deliverySaving?0.5:1}}>{deliverySaving?'Recording...':'Record Delivery'}</button>
</div>}
</div>);}

// ===================== CREATE VIEW =====================
return(<div>
<div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
<button onClick={()=>{setView('list');setFormErr('');}} style={{...sb,background:'transparent',color:t.tm,padding:'4px 0',fontSize:11}}>← Back</button>
<h1 style={{fontSize:20,fontWeight:700,margin:0}}>New Prepaid Order</h1>
</div>
{formErr&&<div style={{background:'rgba(200,55,45,0.08)',border:`1px solid ${t.ac}`,borderRadius:8,padding:12,marginBottom:14,fontSize:12,color:t.ac}}>{formErr}</div>}
<div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:12,marginBottom:16}}>
<div><label style={{fontSize:9,color:t.td,textTransform:'uppercase'}}>Vendor *</label><input value={vendor} onChange={e=>setVendor(e.target.value)} list="vendor-list" placeholder="Type or select vendor..." style={{...si,width:'100%'}} autoFocus/><datalist id="vendor-list">{vendorNames.map(v=><option key={v} value={v}/>)}</datalist></div>
<div><label style={{fontSize:9,color:t.td,textTransform:'uppercase'}}>Date Paid *</label><DateInput value={datePaid} onChange={e=>setDatePaid(e.target.value)} style={{...si,width:'100%'}}/></div>
</div>
<div style={{marginBottom:16}}><label style={{fontSize:9,color:t.td,textTransform:'uppercase'}}>Notes</label><textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={2} placeholder="Optional — PO number, terms, expected delivery..." style={{...si,width:'100%',resize:'vertical',boxSizing:'border-box'}}/></div>
<div style={{marginBottom:16}}>
<label style={{fontSize:9,color:t.td,textTransform:'uppercase',marginBottom:4,display:'block'}}>Invoice Document</label>
<input ref={fileRef} type="file" accept="image/*,.heic,application/pdf" style={{display:'none'}} onChange={e=>{handleFileSelect(e.target.files?.[0]);e.target.value='';}}/>
<input ref={extractRef} type="file" accept="image/*,.heic,application/pdf" style={{display:'none'}} onChange={e=>{handleExtract(e.target.files?.[0]);e.target.value='';}}/>
{extracting?<div style={{textAlign:'center',padding:'20px 0',color:t.tm}}><div style={{fontSize:13,fontWeight:600,marginBottom:4}}>Reading invoice...</div><div style={{fontSize:11,color:t.td}}>Extracting vendor, line items, and prices via OCR</div></div>:
uploadFile?<div style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:t.gn}}>
<span>📄 {uploadFile.name}</span>
<button onClick={()=>{setUploadFile(null);supabase.storage.from('invoices').remove([uploadFile.path]);}} style={{...sb,background:'transparent',color:t.ac,fontSize:10,padding:'2px 6px'}}>Remove</button>
</div>:
<div
  onDragEnter={e=>{e.preventDefault();e.stopPropagation();dragCounter.current++;setDragging(true);}}
  onDragLeave={e=>{e.preventDefault();e.stopPropagation();dragCounter.current--;if(dragCounter.current===0)setDragging(false);}}
  onDragOver={e=>{e.preventDefault();e.stopPropagation();}}
  onDrop={e=>{e.preventDefault();e.stopPropagation();setDragging(false);dragCounter.current=0;const f=e.dataTransfer?.files?.[0];if(f)handleExtract(f);}}
  style={{border:`2px dashed ${dragging?t.bl:t.bd}`,borderRadius:12,padding:'24px 16px',textAlign:'center',cursor:'pointer',background:dragging?'rgba(59,130,246,0.06)':'transparent',transition:'all 0.15s ease'}}
  onClick={()=>extractRef.current?.click()}
>
<div style={{fontSize:24,marginBottom:6,opacity:dragging?1:0.5}}>{dragging?'📥':'📄'}</div>
<div style={{fontSize:13,fontWeight:600,color:dragging?t.bl:t.tx,marginBottom:4}}>{dragging?'Drop invoice here':'Drag invoice here or tap to upload'}</div>
<div style={{fontSize:10,color:t.td,marginBottom:10}}>PDF, JPG, PNG, or HEIC — max 20MB</div>
<div style={{display:'flex',gap:8,justifyContent:'center'}} onClick={e=>e.stopPropagation()}>
<button onClick={()=>extractRef.current?.click()} style={{...sb,background:t.bl,fontSize:11}}>📄 Upload & Extract</button>
<button onClick={()=>fileRef.current?.click()} disabled={uploading} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:11}}>
{uploading?'Uploading...':'📁 Attach only (no OCR)'}
</button>
</div>
</div>}
</div>
<div style={{background:t.cd,borderRadius:12,border:`1px solid ${t.bd}`,padding:18,marginBottom:16}}>
<div style={{fontSize:13,fontWeight:600,marginBottom:12}}>Line Items</div>
<div style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 70px 80px 90px 90px 28px',gap:8,marginBottom:8}}>
{['Description','Ingredient Match','Unit','Qty','Unit Cost','Extended',''].map(h=><div key={h} style={{fontSize:9,color:t.td,textTransform:'uppercase',fontWeight:600}}>{h}</div>)}
</div>
{lines.map(line=>{const ext=(parseFloat(line.qty_ordered)||0)*(parseFloat(line.unit_cost_paid)||0);return<div key={line.key} style={{display:'grid',gridTemplateColumns:'2fr 1.5fr 70px 80px 90px 90px 28px',gap:8,marginBottom:6,alignItems:'center'}}>
<input value={line.description} onChange={e=>updateLine(line.key,'description',e.target.value)} placeholder="Item description" style={{...si,width:'100%',fontSize:11}}/>
<div style={{position:'relative'}}>
{line.matched_ingredient_id?<div style={{display:'flex',alignItems:'center',gap:4}}><span style={{fontSize:10,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{line.matched_ingredient_name}</span><button onClick={()=>{updateLine(line.key,'matched_ingredient_id','');updateLine(line.key,'matched_ingredient_name','');}} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:10,padding:0,flexShrink:0}}>✕</button></div>:
<IngredientDropdown ings={ings} value="" onSelect={ig=>{updateLine(line.key,'matched_ingredient_id',ig.id);updateLine(line.key,'matched_ingredient_name',sn(ig.n));}} placeholder={line.description?'⚠ Match needed':'Search...'} mode="light" excludeCategories={['Needs Review']} minChars={2} maxResults={8} t={t} si={{...si,fontSize:10,borderColor:line.description?t.yl:undefined}}/>}
</div>
<select value={line.unit} onChange={e=>updateLine(line.key,'unit',e.target.value)} style={{...si,width:'100%',fontSize:10}}>{PREPAID_UNITS.map(u=><option key={u}>{u}</option>)}</select>
<input type="number" value={line.qty_ordered} onChange={e=>updateLine(line.key,'qty_ordered',e.target.value)} placeholder="0" min="0" style={{...si,width:'100%',fontSize:11,textAlign:'center',fontFamily:mn}}/>
<div style={{display:'flex',alignItems:'center',gap:2}}><span style={{fontSize:11,color:t.td}}>$</span><input type="number" value={line.unit_cost_paid} onChange={e=>updateLine(line.key,'unit_cost_paid',e.target.value)} placeholder="0.00" min="0" step="0.01" style={{...si,flex:1,fontSize:11,fontFamily:mn}}/></div>
<div style={{fontFamily:mn,fontSize:11,fontWeight:500,color:ext>0?t.tx:t.td}}>${fmt$(ext)}</div>
<button onClick={()=>removeLine(line.key)} style={{background:'none',border:'none',color:t.td,cursor:'pointer',fontSize:14,padding:0}}>✕</button>
</div>;})}
<div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:10}}>
<button onClick={addLine} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`,fontSize:10}}>+ Add line</button>
<div style={{fontFamily:mn,fontSize:14,fontWeight:700}}>Total: <span style={{color:t.gn}}>${fmt$(orderTotal)}</span></div>
</div>
</div>
{detectedFees.length>0&&<div style={{background:t.sf,border:`1px solid ${t.bd}`,borderRadius:8,padding:'12px 16px',marginBottom:16}}>
<div style={{fontSize:11,fontWeight:600,color:t.td,marginBottom:6}}>Fees (logged as expenses, not prepaid line items):</div>
{detectedFees.map((f,i)=><div key={i} style={{display:'flex',justifyContent:'space-between',padding:'4px 0',fontSize:12}}>
<span style={{color:t.tm}}>{f.desc}</span>
<span style={{fontFamily:mn,fontWeight:500}}>${fmt$(f.amt)}</span>
</div>)}
<div style={{display:'flex',justifyContent:'space-between',paddingTop:6,borderTop:`1px solid ${t.bd}`,marginTop:4,fontSize:12,fontWeight:600}}>
<span>Total fees</span><span style={{fontFamily:mn}}>${fmt$(detectedFees.reduce((s,f)=>s+f.amt,0))}</span>
</div>
</div>}
<div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
<button onClick={()=>{setView('list');setFormErr('');setDetectedFees([]);}} style={{...sb,background:t.sf,color:t.tm,border:`1px solid ${t.bd}`}}>Cancel</button>
<button onClick={saveOrder} disabled={saving} style={{...sb,background:t.gn,padding:'10px 24px',opacity:saving?0.5:1}}>{saving?'Saving...':'Save Prepaid Order'}</button>
</div>
</div>);
}
